// AuthCallback.jsx - Handles Google OAuth callback from Emergent Auth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getBackendUrl } from '../config/api';
import { clientHeaders } from '../utils/clientHeaders';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Processing authentication...');
  const [errorDetails, setErrorDetails] = useState(null);
  const retryCount = useRef(0);
  
  // Get auth context
  const auth = useAuth();

  useEffect(() => {
    // Prevent double processing
    if (hasProcessed.current) {
      console.log('[AuthCallback] Already processed, skipping');
      return;
    }
    
    const processAuth = async () => {
      try {
        console.log('[AuthCallback] ====== STARTING AUTH CALLBACK ======');
        console.log('[AuthCallback] Full URL:', window.location.href);
        console.log('[AuthCallback] Origin:', window.location.origin);
        console.log('[AuthCallback] Pathname:', window.location.pathname);
        console.log('[AuthCallback] Hash:', window.location.hash);
        console.log('[AuthCallback] Hash length:', window.location.hash?.length);
        console.log('[AuthCallback] Search:', window.location.search);
        console.log('[AuthCallback] BACKEND_URL:', getBackendUrl());
        console.log('[AuthCallback] Auth context available:', !!auth);
        
        // Extract session_id from URL fragment
        let hash = window.location.hash;
        
        // Sometimes the hash might not be immediately available - wait and retry
        if ((!hash || hash === '' || hash === '#') && retryCount.current < 5) {
          retryCount.current++;
          console.log(`[AuthCallback] No hash found (hash="${hash}"), retry ${retryCount.current}/5 in 300ms`);
          await new Promise(resolve => setTimeout(resolve, 300));
          hash = window.location.hash;
          console.log(`[AuthCallback] After wait, hash="${hash}"`);
          if (!hash || hash === '' || hash === '#') {
            setTimeout(() => processAuth(), 300);
            return;
          }
        }
        
        // Also check URL search params (in case Emergent Auth uses query params instead)
        const searchParams = new URLSearchParams(window.location.search);
        const sessionIdFromSearch = searchParams.get('session_id');
        
        let sessionId = null;
        
        // Try to get session_id from hash first
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        if (sessionIdMatch) {
          sessionId = sessionIdMatch[1];
          console.log('[AuthCallback] Found session_id in hash');
        } else if (sessionIdFromSearch) {
          sessionId = sessionIdFromSearch;
          console.log('[AuthCallback] Found session_id in search params');
        }
        
        if (!sessionId) {
          console.error('[AuthCallback] ❌ No session_id found anywhere in URL after all retries');
          console.error('[AuthCallback] Final hash value:', hash);
          console.error('[AuthCallback] Final search value:', window.location.search);
          console.error('[AuthCallback] Full href:', window.location.href);
          setStatus('No session found. Redirecting...');
          setErrorDetails(`Hash: "${hash}", Search: "${window.location.search}"`);
          hasProcessed.current = true;
          const returnPath = sessionStorage.getItem('authReturnPath') || '/';
          setTimeout(() => navigate(returnPath, { replace: true, state: { error: 'No session_id in URL' } }), 2000);
          return;
        }

        // Mark as processed
        hasProcessed.current = true;

        console.log('[AuthCallback] ✓ Found session_id:', sessionId.substring(0, 20) + '...');
        setStatus('Verifying with server...');

        // Exchange session_id for user data
        const apiUrl = `${getBackendUrl()}/api/auth/session`;
        console.log('[AuthCallback] Calling backend:', apiUrl);
        
        let response;
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...clientHeaders(),
            },
            // NOTE: Removed credentials: 'include' to avoid CORS issues with wildcard origin
            // We use localStorage for token storage instead of cookies
            body: JSON.stringify({ session_id: sessionId }),
          });
        } catch (fetchError) {
          console.error('[AuthCallback] ❌ Fetch error:', fetchError);
          throw new Error(`Network error: ${fetchError.message}`);
        }

        console.log('[AuthCallback] Backend response status:', response.status);
        console.log('[AuthCallback] Backend response ok:', response.ok);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[AuthCallback] ❌ Backend error response:', errorText);
          throw new Error(`Backend error: ${response.status} - ${errorText}`);
        }

        let data;
        try {
          data = await response.json();
          console.log('[AuthCallback] ✓ Parsed response JSON successfully');
        } catch (jsonError) {
          console.error('[AuthCallback] ❌ JSON parse error:', jsonError);
          throw new Error(`Failed to parse response: ${jsonError.message}`);
        }
        
        let user = data.user;
        const sessionToken = data.session_token;

        console.log('[AuthCallback] ✓ Received user:', user?.email || 'NO USER');
        console.log('[AuthCallback] ✓ Received sessionToken:', sessionToken ? 'YES' : 'NO');
        
        if (!user) {
          console.error('[AuthCallback] ❌ No user in response');
          throw new Error('No user data in response');
        }
        
        if (!sessionToken) {
          console.error('[AuthCallback] ❌ No session_token in response');
          throw new Error('No session_token in response');
        }
        
        setStatus('Saving session...');

        // CRITICAL: Store session token in localStorage IMMEDIATELY
        try {
          localStorage.setItem('jyotish_session_token', sessionToken);
          console.log('[AuthCallback] ✅ Session token stored in localStorage');
        } catch (storageError) {
          console.error('[AuthCallback] ❌ localStorage error for token:', storageError);
          // Continue anyway - might be incognito with storage disabled
        }
        
        // Store user data for quick restoration
        try {
          localStorage.setItem('jyotish_auth_user', JSON.stringify(user));
          console.log('[AuthCallback] ✅ User data cached in localStorage');
        } catch (storageError) {
          console.error('[AuthCallback] ❌ localStorage error for user:', storageError);
          // Continue anyway
        }

        // Clear the URL fragment
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch (historyError) {
          console.warn('[AuthCallback] Could not clear URL fragment:', historyError);
        }

        // =====================================================
        // GUEST-TO-USER DATA PERSISTENCE - AUTH HANDSHAKE
        // =====================================================
        // Check for guest birth data in BOTH localStorage (persistent) and sessionStorage
        const guestBirthDataPersistent = localStorage.getItem('guest_birth_data_pending_sync');
        const guestBirthDataSession = sessionStorage.getItem('guestBirthData');
        const guestDataRaw = guestBirthDataPersistent || guestBirthDataSession;

        // Only sync a guest blob into accounts that genuinely have NO birth
        // details yet. Gating on real emptiness (not just !onboarding_complete)
        // prevents a stale funnel blob from overwriting a returning user's
        // existing date/time/place. For such returning users, clear the blob so
        // it can't resurface on a later login.
        const alreadyHasBirth = !!(user.date && user.time && user.place);
        if (guestDataRaw && alreadyHasBirth) {
          localStorage.removeItem('guest_birth_data_pending_sync');
          sessionStorage.removeItem('guestBirthData');
        }

        if (guestDataRaw && !alreadyHasBirth && !user.onboarding_complete) {
          try {
            const birthData = JSON.parse(guestDataRaw);
            console.log('[AuthCallback] Found guest birth data pending sync:', birthData);
            setStatus('Syncing your birth data...');
            
            // Use the new sync-guest-profile endpoint for complete profile creation with Dasha
            const syncResponse = await fetch(`${getBackendUrl()}/api/auth/sync-guest-profile`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
                ...clientHeaders(),
              },
              body: JSON.stringify({
                gender: birthData.gender,
                date: birthData.date,
                time: birthData.time,
                place: birthData.place,
                latitude: birthData.latitude,
                longitude: birthData.longitude,
                name: birthData.name || user.name,
                lagna_preview: birthData.lagna_preview,
                // Pass the anonymous guest id so the backend can delete the
                // guest_* row in `users` and merge its refs into the new
                // user_* row — fixes duplicate admin entries on signup.
                guest_user_id: localStorage.getItem('guest_user_id') || birthData.user_id || null,
              }),
            });

            if (syncResponse.ok) {
              const syncData = await syncResponse.json();
              user = syncData.user;
              localStorage.setItem('jyotish_auth_user', JSON.stringify(user));

              // CLEANUP: Clear guest data ONLY after successful 200 OK
              localStorage.removeItem('guest_birth_data_pending_sync');
              localStorage.removeItem('guest_user_id');
              sessionStorage.removeItem('guestBirthData');
              
              console.log('[AuthCallback] ✅ Guest data synced, profile complete with Dasha-Lagna');
              console.log('[AuthCallback] User Dasha status:', user.current_dasha);
            } else {
              // Sync failed - try legacy endpoint as fallback
              console.warn('[AuthCallback] New sync failed, trying legacy complete-onboarding');
              
              const onboardingResponse = await fetch(`${getBackendUrl()}/api/auth/complete-onboarding`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${sessionToken}`,
                },
                body: JSON.stringify({
                  gender: birthData.gender,
                  date: birthData.date,
                  time: birthData.time,
                  place: birthData.place,
                  latitude: birthData.latitude,
                  longitude: birthData.longitude,
                }),
              });

              if (onboardingResponse.ok) {
                const onboardingData = await onboardingResponse.json();
                user = onboardingData.user;
                localStorage.setItem('jyotish_auth_user', JSON.stringify(user));
                localStorage.removeItem('guest_birth_data_pending_sync');
                sessionStorage.removeItem('guestBirthData');
                console.log('[AuthCallback] ✅ Birth data saved via legacy endpoint');
              } else {
                const errText = await onboardingResponse.text();
                console.error('[AuthCallback] ⚠️ Failed to save birth data:', errText);
                // Keep guest data for retry on next auth
              }
            }
          } catch (e) {
            console.error('[AuthCallback] ⚠️ Error syncing birth data:', e);
            // Keep guest data for retry - don't clear localStorage
          }
        }

        // Update AuthContext state
        setStatus('Updating app state...');
        if (auth?.handleAuthCallback) {
          console.log('[AuthCallback] Calling handleAuthCallback...');
          try {
            await auth.handleAuthCallback(sessionToken, user);
            console.log('[AuthCallback] ✅ AuthContext updated');
          } catch (authError) {
            console.error('[AuthCallback] ⚠️ handleAuthCallback error:', authError);
            // Don't throw - localStorage has the data, will work on next page load
          }
        } else {
          console.warn('[AuthCallback] ⚠️ handleAuthCallback not available');
        }

        // Get return path
        let returnPath = sessionStorage.getItem('authReturnPath') || '/';
        const returnView = sessionStorage.getItem('authReturnView') || 'dashboard';
        
        // Clear session storage
        try {
          sessionStorage.removeItem('authReturnPath');
          sessionStorage.removeItem('authReturnView');
          sessionStorage.removeItem('authFeature');
        } catch (e) {
          console.warn('[AuthCallback] Could not clear sessionStorage');
        }

        // Redirect to onboarding if needed
        // Check if user has birth details - this is what matters for dashboard access
        // Even if onboarding_complete is false, if they have birth data, they can use the app
        const hasBirthDetails = user.date && user.time && user.place;
        
        if (!user.onboarding_complete && !hasBirthDetails) {
          console.log('[AuthCallback] User needs onboarding - no birth details');
          returnPath = '/onboarding';
        } else if (!user.onboarding_complete && hasBirthDetails) {
          console.log('[AuthCallback] User has birth details but onboarding_complete is false - going to dashboard anyway');
        }

        console.log('[AuthCallback] ====== AUTH CALLBACK COMPLETE - SUCCESS ======');
        console.log('[AuthCallback] Navigating to:', returnPath);
        setStatus('Success! Redirecting...');
        
        // Small delay to ensure state updates
        await new Promise(resolve => setTimeout(resolve, 200));
        
        navigate(returnPath, { 
          replace: true, 
          state: { 
            user,
            sessionToken,
            resumeView: returnView,
            justAuthenticated: true
          } 
        });

      } catch (error) {
        console.error('[AuthCallback] ====== AUTH CALLBACK FAILED ======');
        console.error('[AuthCallback] Error:', error);
        console.error('[AuthCallback] Error message:', error.message);
        console.error('[AuthCallback] Error stack:', error.stack);
        
        setStatus('Authentication failed. Redirecting...');
        setErrorDetails(error.message);
        hasProcessed.current = true;
        
        const returnPath = sessionStorage.getItem('authReturnPath') || '/';
        try {
          sessionStorage.removeItem('authReturnPath');
          sessionStorage.removeItem('authReturnView');
          sessionStorage.removeItem('authFeature');
        } catch (cleanupError) {
          // Ignore cleanup errors - non-critical
          console.debug('[AuthCallback] Cleanup warning:', cleanupError);
        }
        
        setTimeout(() => {
          navigate(returnPath, { replace: true, state: { error: error.message } });
        }, 2500);
      }
    };

    processAuth();
  }, [navigate, auth]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d0d1a] via-[#1a1a2e] to-[#0d0d1a] flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
        <p className="text-white/70">{status}</p>
        {errorDetails && (
          <p className="text-red-400/70 text-xs mt-2 break-all">{errorDetails}</p>
        )}
        <p className="text-white/40 text-xs mt-2">Please wait...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
